"use client";

import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Image from "../../../components/common/Image";
import { NameWithLogo } from "../../../types";
import { FaChartLine, FaCogs, FaDesktop, FaLaptopCode, FaMobile, FaSearch, FaShieldAlt, FaShoppingCart, FaTools } from "react-icons/fa";
import HeroSection from "../../../components/common/HeroSection";

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const process = [
  {
    step: "01",
    title: "Assessment & Discovery",
    description: "Comprehensive analysis of your current technology infrastructure and business objectives.",
    icon: <FaSearch className="w-8 h-8" />,
    color: "from-indigo-600 to-indigo-800"
  },
  {
    step: "02",
    title: "Strategy Development",
    description: "Creating tailored IT strategies and roadmaps aligned with your business goals.",
    icon: <FaChartLine className="w-8 h-8" />,
    color: "from-blue-600 to-blue-800"
  },
  {
    step: "03",
    title: "Solution Design",
    description: "Designing comprehensive solutions to address your specific technology needs.",
    icon: <FaCogs className="w-8 h-8" />,
    color: "from-purple-600 to-purple-800"
  },
  {
    step: "04",
    title: "Implementation Planning",
    description: "Detailed planning for seamless integration of new technology solutions.",
    icon: <FaTools className="w-8 h-8" />,
    color: "from-indigo-500 to-purple-600"
  },
  {
    step: "05",
    title: "Execution & Support",
    description: "Expert guidance throughout implementation and ongoing support for optimal results.",
    icon: <FaDesktop className="w-8 h-8" />,
    color: "from-blue-500 to-indigo-600"
  }
];
export default function ConsultingPage() {
  return (
    <main className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <HeroSection
          title="IT Consulting"
          description="Strategic technology consulting to transform your business and drive sustainable growth"
          showPattern={true}
        />
        
      </section>

      {/* Services Overview */}
      <section className="pb-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="space-y-6"
            >
              <h2 className="text-3xl font-bold">Strategic IT Solutions for Your Business</h2>
              <p className="text-lg text-gray-600">
                We provide expert IT consulting services that help businesses optimize their technology infrastructure, 
                improve operational efficiency, and achieve their strategic goals. Our experienced consultants work 
                closely with you to develop tailored solutions that drive innovation and growth.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center mt-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-gray-700">Comprehensive technology assessment and planning</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center mt-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-gray-700">Strategic digital transformation roadmaps</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center mt-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-gray-700">IT infrastructure optimization</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center mt-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-gray-700">Data-driven decision making strategies</p>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="relative"
            >
              <div className="relative h-[400px] w-full rounded-3xl overflow-hidden shadow-2xl">
                <Image
                  src="/images/services/consulting.png"
                  alt="IT Consulting"
                  fill
                  className="object-cover p-12"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-indigo-600 rounded-full opacity-20"></div>
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-purple-600 rounded-full opacity-20"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-bold mb-6">Our Consulting Services</h2>
            <p className="text-lg text-gray-600">
              We provide comprehensive IT consulting services to help organizations optimize their technology investments.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <FaChartLine className="w-6 h-6" />,
                title: "Strategic Planning",
                description: "Develop comprehensive IT strategies aligned with your business objectives and growth plans."
              },
              {
                icon: <FaCogs className="w-6 h-6" />,
                title: "Process Optimization",
                description: "Streamline operations and improve efficiency through technology-driven process improvements."
              },
              {
                icon: <FaDesktop className="w-6 h-6" />,
                title: "Digital Transformation",
                description: "Guide your organization through digital transformation initiatives for competitive advantage."
              },
              {
                icon: <FaShieldAlt className="w-6 h-6" />,
                title: "Security Consulting",
                description: "Enhance your cybersecurity posture with comprehensive security assessments and strategies."
              },
              {
                icon: <FaLaptopCode className="w-6 h-6" />,
                title: "Technology Assessment",
                description: "Evaluate and optimize your current technology stack for maximum effectiveness."
              },
              {
                icon: <FaTools className="w-6 h-6" />,
                title: "Implementation Support",
                description: "Expert guidance and support throughout technology implementation projects."
              }
            ].map((service, index) => (
              <motion.div
                key={service.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeIn}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="bg-gradient-to-br from-white to-gray-50 p-8 rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 
                  text-white flex items-center justify-center mb-6">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold mb-4">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-bold mb-6">Our Consulting Process</h2>
            <p className="text-lg text-gray-600">
              We follow a systematic approach to deliver strategic IT solutions that transform your business operations.
            </p>
          </motion.div>

          <div className="relative max-w-5xl mx-auto h-auto md:h-[800px] perspective-[2000px]">
            { process.map((process, index) => (
              <motion.div
                key={process.step}
                className="block mb-6 md:mb-4 md:absolute md:inset-x-0"
                style={{
                  top: `${index * 140}px`,
                  transformStyle: "preserve-3d"
                }}
                initial={{ opacity: 0, rotateX: 45, y: 100 }}
                whileInView={{
                  opacity: 1,
                  rotateX: 0,
                  y: 0
                }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.8,
                  delay: index * 0.2,
                  ease: "easeOut"
                }}
                whileHover={{
                  scale: 1.02,
                  rotateX: -5,
                  transition: { duration: 0.3 }
                }}
              >
                <div className={`bg-gradient-to-r ${process.color} p-6 md:p-8 rounded-3xl shadow-2xl 
                  text-white relative overflow-hidden group`}>
                  <div className="absolute -right-8 -top-8 w-32 h-32 bg-white/10 rounded-full blur-2xl 
                    group-hover:scale-150 transition-transform duration-500" />
                  
                  <div className="flex flex-col md:flex-row items-start gap-4 md:gap-6">
                    <div className="flex-shrink-0 w-12 h-12 md:w-16 md:h-16 bg-white/20 rounded-2xl flex items-center 
                      justify-center text-2xl md:text-3xl backdrop-blur-sm">
                      {process.icon}
                    </div>
                    <div className="space-y-2 md:space-y-4">
                      <div className="flex items-center gap-2 md:gap-3">
                        <span className="text-3xl md:text-5xl font-bold opacity-50">{process.step}</span>
                        <h3 className="text-xl md:text-2xl font-bold">{process.title}</h3>
                      </div>
                      <p className="text-base md:text-lg text-white/80">{process.description}</p>
                    </div>
                  </div>

                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0"
                    initial={{ x: "-100%" }}
                    whileHover={{ x: "100%" }}
                    transition={{ duration: 1 }}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-indigo-600 to-purple-600 text-white relative z-10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h2
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="text-3xl md:text-4xl font-bold mb-6"
            >
              Ready to Start Your Web Development Project?
            </motion.h2>
            <motion.p
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ delay: 0.1 }}
              className="text-lg text-indigo-100 mb-10"
            >
              Let's discuss how we can help you achieve your business goals with a custom web solution.
            </motion.p>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ delay: 0.2 }}
            >
              <Link
                to="/contact"
                className="inline-flex items-center px-8 py-4 rounded-full bg-white text-indigo-600 
                  hover:shadow-lg hover:shadow-indigo-700/20 transition-all duration-300 text-lg font-semibold"
              >
                Get a Free Consultation
                <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}